import { useState } from "react";
import { ExternalLink, Globe, Pencil, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, previewUrl } from "@/lib/api";
import { useAuth } from "@/App";

function Preview({ site }) {
  const [failed, setFailed] = useState(false);
  const src = previewUrl(site);
  if (!src || failed)
    return (
      <div className="w-full h-full grid place-items-center bg-[linear-gradient(135deg,#111827,#1E293B)]">
        <Globe className="w-8 h-8 text-slate-600" />
      </div>
    );
  return (
    <img src={src} alt={site.title} loading="lazy" onError={() => setFailed(true)}
      className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500" />
  );
}

export const SiteTile = ({ site, index, isAdmin, onEdit, dragHandle, tp = "site-tile" }) => {
  const { user, setUser } = useAuth();
  const isFav = (user.favorites || []).includes(site.id);
  const hasUrl = Boolean(site.url);
  const host = hasUrl ? site.url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "link da configurare";

  const toggleFav = async () => {
    try {
      const { data } = await api.post(`/favorites/${site.id}`);
      setUser((u) => ({ ...u, favorites: data.favorites }));
      toast.success(isFav ? "Rimosso dai preferiti" : "Aggiunto ai preferiti");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <article data-testid={`${tp}-card-${site.position}`} style={{ animationDelay: `${index * 50}ms` }}
      className="rise group relative flex flex-col rounded-xl overflow-hidden bg-slate-800/50 backdrop-blur-md border border-slate-700/60 hover:border-blue-500/60 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(37,99,235,0.35)] transition-[border-color,transform,box-shadow] duration-200">
      <a href={hasUrl ? site.url : undefined} target="_blank" rel="noopener noreferrer"
        data-testid={`${tp}-open-button-${site.position}`}
        className={`block aspect-video overflow-hidden bg-slate-900 border-b border-slate-700/50 ${hasUrl ? "" : "pointer-events-none"}`}>
        <Preview site={site} />
      </a>
      <div className="absolute top-2 left-2 flex items-center gap-1">
        {dragHandle && (
          <button {...dragHandle} aria-label="Trascina per riordinare" data-testid={`site-tile-drag-handle-${site.position}`}
            className="p-1 rounded bg-black/60 backdrop-blur text-slate-200 cursor-grab active:cursor-grabbing hover:bg-blue-600 touch-none transition-colors">
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        )}
        <span className="font-mono text-[10px] text-slate-300 bg-black/60 backdrop-blur px-1.5 py-0.5 rounded">
          {String(site.position).padStart(2, "0")}
        </span>
      </div>
      <div className="absolute top-2 right-2 flex items-center gap-1">
        <button onClick={toggleFav} data-testid={`${tp}-fav-button-${site.position}`} aria-label={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          aria-pressed={isFav} title={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          className={`p-1.5 rounded-md bg-black/60 backdrop-blur transition-[opacity,color,background-color] hover:bg-black/80 ${isFav ? "text-amber-400 opacity-100" : "text-slate-200 opacity-100 sm:opacity-0 group-hover:opacity-100 hover:text-amber-300"}`}>
          <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-400" : ""}`} />
        </button>
        {isAdmin && (
          <button onClick={onEdit} data-testid={`${tp}-edit-button-${site.position}`} aria-label="Modifica"
            className="p-1.5 rounded-md bg-black/60 backdrop-blur text-slate-200 opacity-100 sm:opacity-0 group-hover:opacity-100 hover:bg-blue-600 transition-[opacity,background-color]">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <h3 className="font-semibold text-sm text-slate-100 truncate" data-testid={`${tp}-title-${site.position}`}>{site.title}</h3>
        <a href={hasUrl ? site.url : undefined} target="_blank" rel="noopener noreferrer" data-testid={`${tp}-link-${site.position}`}
          className={`font-mono text-xs truncate flex items-center gap-1 ${hasUrl ? "text-blue-400 hover:text-blue-300" : "text-slate-500 pointer-events-none"} transition-colors`}>
          {hasUrl && <ExternalLink className="w-3 h-3 shrink-0" />}<span className="truncate">{host}</span>
        </a>
        <p className="text-xs text-slate-400 leading-snug line-clamp-2 min-h-[2.5rem]" data-testid={`${tp}-desc-${site.position}`}>
          {site.description}
        </p>
      </div>
    </article>
  );
};
